/**
 * LOGOI LAB - Layer 2: Levin Bioelectric Pattern Detection
 * Detects pathological patterns and negative attractors
 * 
 * @author Dr. José Manuel Cadena Ortiz de Montellano
 * @framework Cadena Strategic Systems
 */

import type { ConsciousnessState, LevinSignal } from '../types';

/**
 * Compute Levin Bioelectric Signals
 * 
 * Detects early warning patterns indicating system distress:
 * - Depressive signal
 * - Anxiety signal
 * - Spiritual disconnection
 * - Relational isolation
 * - Overperformance/burnout
 * - Negative attractor A⁻
 * 
 * @param s - Current consciousness state
 * @param Lambda - Current Logos alignment value
 * @returns Array of detected signals with severity and recommendations
 */
export function computeLevinSignals(s: ConsciousnessState, Lambda: number): LevinSignal[] {
  const signals: LevinSignal[] = [];
  const state = s as any;
  
  // Depressive Pattern
  if (state.joy < 0.30 && state.energy < 0.35 && state.meaning < 0.30) {
    signals.push({ 
      type: "SEÑAL DEPRESIVA", 
      signalKey: "depressive",
      severity: 0.85, 
      icon: "🌑", 
      color: "#6b21a8",
      desc: "Baja alegría + baja energía + pérdida de sentido. Patrón bioelectrico de colapso motivacional.",
      action: "Activación controlada: movimiento físico (10 min), contacto social (1 persona), gratitud escrita (3 cosas).",
      logosLink: "La desconexión del Logos produce pérdida de sentido. Reconecta primero." 
    });
  }
  
  // Anxiety Pattern
  if (state.peace < 0.30 && state.clarity < 0.35 && state.focus < 0.35) {
    signals.push({ 
      type: "SEÑAL DE ANSIEDAD", 
      signalKey: "anxiety",
      severity: 0.80, 
      icon: "⚡", 
      color: "#ef4444",
      desc: "Baja paz + baja claridad + bajo enfoque. Sobrecarga del sistema simpático.",
      action: "Regulación vagal: respiración 4-7-8, meditación 5 min, reducir estímulos externos.",
      logosLink: "La ansiedad es ruido que ahoga la señal. Reduce el ruido primero." 
    });
  }
  
  // Spiritual Disconnection
  if (state.faith < 0.25 && state.presence < 0.30 && state.meditation < 0.20) {
    signals.push({ 
      type: "DESCONEXIÓN ESPIRITUAL", 
      signalKey: "spiritual",
      severity: 0.70, 
      icon: "◌", 
      color: "#71717a",
      desc: "Fe, presencia y meditación en mínimos. Canal de recepción del Logos obstruido.",
      action: "Reconexión: 10 min de silencio contemplativo, lectura inspiracional, tiempo en naturaleza.",
      logosLink: "CRÍTICO: El canal receptor está cerrado. Sin canal, no hay señal. Sin señal, todo colapsa." 
    });
  }
  
  // Relational Isolation
  if (state.family < 0.30 && state.friendship < 0.30 && state.community < 0.25) {
    signals.push({ 
      type: "AISLAMIENTO RELACIONAL", 
      signalKey: "isolation",
      severity: 0.75, 
      icon: "🔒", 
      color: "#92400e",
      desc: "Vínculos relacionales en mínimos. El sistema pierde retroalimentación social.",
      action: "Reconexión gradual: una llamada, un café, un acto de servicio pequeño.",
      logosLink: "El Logos se manifiesta a través del otro. El aislamiento corta un canal de recepción." 
    });
  }
  
  // Overperformance/Burnout Pattern
  if (state.energy < 0.25 && state.sleep < 0.30 && state.exercise < 0.20 && state.focus > 0.60) {
    signals.push({ 
      type: "SOBRE-RENDIMIENTO", 
      signalKey: "burnout",
      severity: 0.80, 
      icon: "🔥", 
      color: "#ea580c",
      desc: "Enfoque alto con energía colapsada. El sistema rinde pero se consume. Atractor frágil.",
      action: "Descanso obligatorio. Reducir compromisos. Priorizar recuperación sobre productividad.",
      logosLink: "El Logos no demanda autoDestrucción. La sostenibilidad es señal; el burnout es ruido." 
    });
  }
  
  // Dysbiosis — Gut-brain axis disruption
  if (state.gut_resonance < 0.25 && state.nourishment < 0.30 && state.energy < 0.35) {
    signals.push({ 
      type: "DISBIOSIS ENTÉRICA", 
      signalKey: "dysbiosis",
      severity: 0.70, 
      icon: "🌿", 
      color: "#84cc16",
      desc: "Resonancia intestinal baja + nutrición deficiente + energía colapsada. El eje intestino-cerebro está comprometido.",
      action: "Alimentos fermentados (kefir, kimchi), fibra prebiótica, reducir procesados. Masticar lento.",
      logosLink: "El cuerpo es templo. Un templo descuidado no puede recibir la señal del Logos." 
    });
  }
  
  // Hedonic Adaptation — Eating without presence
  if (state.taste_presence < 0.25 && state.food_harmony < 0.30 && state.joy > 0.60) {
    signals.push({ 
      type: "ADAPTACIÓN HEDÓNICA ALIMENTARIA", 
      signalKey: "hedonicFood",
      severity: 0.65, 
      icon: "🍭", 
      color: "#f59e0b",
      desc: "Placer alto pero sin presencia gustativa ni armonía. Comer automático, no consciente.",
      action: "Practica una comida en silencio. Saborea 3 bocados con atención plena. Gratitud antes de comer.",
      logosLink: "El alimento sagrado requiere presencia. Sin ella, nutres el cuerpo pero no el espíritu." 
    });
  }
  
  // Entropic Intoxication — Total alimentary collapse
  if (state.gut_resonance < 0.20 && state.nourishment < 0.25 && state.peace < 0.30) {
    signals.push({ 
      type: "INTOXICACIÓN ENTRÓPICA", 
      signalKey: "entropicIntox",
      severity: 0.90, 
      icon: "☠", 
      color: "#dc2626",
      desc: "ALERTA: Colapso alimentario total. Resonancia intestinal crítica + nutrición mínima + paz destruida. El sistema se intoxica desde dentro.",
      action: "PROTOCOLO: Ayuno intermitente 16h, hidratación intensa, caldo de hueso, eliminar azúcar/alcohol. Consulta médica si persiste.",
      logosLink: "La intoxicación entrópica es ruido químico que ahoga toda señal. El Logos no puede operar en un templo envenenado." 
    });
  }
  
  // CRITICAL: Negative Attractor Detection A⁻
  if (Lambda < 0.20 && state.peace < 0.25 && state.meaning < 0.25) {
    signals.push({ 
      type: "⚠ ATRACTOR NEGATIVO A⁻", 
      signalKey: "negAttractor",
      severity: 0.95, 
      icon: "🕳", 
      color: "#dc2626",
      desc: "ALERTA: Espiral entrópica detectada. Λ̇ < 0 con S̈ > 0. El sistema está convergiendo hacia un basin de atracción patológico — desconexión auto-reforzante del Logos.",
      action: "PROTOCOLO DE ESCAPE: Intervención externa necesaria. Busca a una persona de confianza. Rompe la rutina. Cualquier acción que te saque del loop actual.",
      logosLink: "A⁻ es la antítesis del Logos: un atractor que se alimenta de su propia desconexión. Cada paso hacia A⁻ hace más difícil el retorno. ACT NOW." 
    });
  }
  
  return signals;
}

/**
 * Check if system has critical signals
 */
export function hasCriticalSignals(signals: LevinSignal[]): boolean {
  return signals.some(s => s.severity > 0.80);
}

/**
 * Check if negative attractor is detected
 */
export function hasNegativeAttractor(signals: LevinSignal[]): boolean {
  return signals.some(s => s.type.includes("ATRACTOR NEGATIVO"));
}
